import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import { useGetEligibleAgentTemplatesQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import buildAgentTemplateSelectionInsights from 'utils/buildAgentTemplateSelectionInsights';

import type IEligibleAgentTemplate from 'store/slices/valueChainMap/interfaces/IEligibleAgentTemplate';
import type { TAgentTemplateConfirmedOutputKind } from 'utils/buildAgentTemplateSelectionInsights';

import styles from './sass/EligibleAgentTemplateSelect.module.scss';

export interface IEligibleAgentTemplateSelectProps {
  valueChainMapId: number,
  uiAgentType?: string,
  type?: string,
  subType?: string,
  value: number | null,
  onChange: (templateId: number | null, template?: IEligibleAgentTemplate | null) => void,
  label?: string,
  disabled?: boolean,
  required?: boolean,
  className?: string,
  skip?: boolean,
}

const CONFIRMED_OUTPUT_LABEL_KEYS: Record<TAgentTemplateConfirmedOutputKind, string> = {
  materials: 'valueChainMap.agentTemplateConfirmed.materials',
  suppliers: 'valueChainMap.agentTemplateConfirmed.suppliers',
  findings: 'valueChainMap.agentTemplateConfirmed.findings',
  signals: 'valueChainMap.agentTemplateConfirmed.signals',
  regulations: 'valueChainMap.agentTemplateConfirmed.regulations',
  exposures: 'valueChainMap.agentTemplateConfirmed.exposures',
  entities: 'valueChainMap.agentTemplateConfirmed.entities',
};

function AgentTemplatePanel({
  template,
  selected,
  disabled,
  onSelect,
}: {
  template: IEligibleAgentTemplate,
  selected: boolean,
  disabled: boolean,
  onSelect: () => void,
}): React.JSX.Element {
  const { t } = useTranslation();
  const insights = useMemo(
    () => buildAgentTemplateSelectionInsights(template),
    [template],
  );
  const descriptionText = template.description?.trim() || '';

  return (
    <article
      className={clsx(styles.panel, {
        [styles.panelSelected]: selected,
        [styles.panelDisabled]: disabled,
        [styles.panelNotRunnable]: !template.runnable,
      })}
    >
      <button
        type="button"
        className={styles.panelSelect}
        onClick={onSelect}
        disabled={disabled}
        aria-pressed={selected}
      >
        <div className={styles.panelMain}>
          <div className={styles.panelTitleRow}>
            <h3 className={styles.panelTitle}>{template.name}</h3>
            {selected ? (
              <span className={styles.selectedBadge}>
                {t('valueChainMap.agentTemplateSelected')}
              </span>
            ) : null}
          </div>

          <div className={styles.badgeRow}>
            <span className={clsx(styles.badge, styles.badgeNeutral)}>
              {t('valueChainMap.agentTemplateVersion', { version: template.promptVersion })}
            </span>
            <span className={clsx(
              styles.badge,
              template.runnable ? styles.badgeSuccess : styles.badgeWarning,
            )}
            >
              {template.runnable
                ? t('valueChainMap.agentTemplateRunnable')
                : t('valueChainMap.agentTemplateNotRunnable')}
            </span>
            <span className={clsx(styles.badge, styles.badgeNeutral)}>
              {t('valueChainMap.agentTemplateRunsBadge', { count: insights.runCount })}
            </span>
            {insights.successRatePercent != null ? (
              <span className={clsx(styles.badge, styles.badgeNeutral)}>
                {t('valueChainMap.agentTemplateSuccessBadge', { value: insights.successRatePercent })}
              </span>
            ) : null}
            <span className={clsx(styles.badge, styles.badgeNeutral)}>
              {t(CONFIRMED_OUTPUT_LABEL_KEYS[insights.confirmedOutputKind], {
                count: insights.confirmedOutputCount,
              })}
            </span>
          </div>

          {descriptionText ? (
            <p className={styles.description}>{descriptionText}</p>
          ) : null}
        </div>
      </button>
    </article>
  );
}

function EligibleAgentTemplateSelect({
  valueChainMapId,
  uiAgentType,
  type,
  subType,
  value,
  onChange,
  label,
  disabled = false,
  required = true,
  className,
  skip = false,
}: IEligibleAgentTemplateSelectProps): React.JSX.Element {
  const { t } = useTranslation();
  const canQuery = !skip
    && Number.isInteger(valueChainMapId)
    && valueChainMapId > 0
    && Boolean(uiAgentType || (type && subType));

  const { data, isFetching, isError } = useGetEligibleAgentTemplatesQuery(
    {
      valueChainMapId,
      ...(uiAgentType ? { uiAgentType } : {}),
      ...(type ? { type } : {}),
      ...(subType ? { subType } : {}),
    },
    { skip: !canQuery },
  );

  const templates = useMemo(() => data?.data ?? [], [data?.data]);
  const showInsightsNote = templates.some((template) => !template.metrics);

  return (
    <div className={className || styles.root}>
      {label ? (
        <div className={styles.labelRow}>
          <span className={styles.label}>
            {label}
            {required ? <span className={styles.requiredMark}>*</span> : null}
          </span>
        </div>
      ) : null}

      {isFetching ? (
        <div className={styles.loader}>
          <SmallLoader size={28} />
        </div>
      ) : null}

      {isError ? (
        <p className={styles.error}>{t('valueChainMap.agentTemplateLoadError')}</p>
      ) : null}

      {!isFetching && canQuery && !templates.length ? (
        <p className={styles.hint}>{t('valueChainMap.agentTemplateEmpty')}</p>
      ) : null}

      {!isFetching && templates.length ? (
        <div className={styles.panelList} role="listbox" aria-label={label || t('valueChainMap.agentTemplateLabel')}>
          {templates.map((template) => (
            <AgentTemplatePanel
              key={template.id}
              template={template}
              selected={value === template.id}
              disabled={disabled}
              onSelect={() => {
                if (required && value === template.id) {
                  return;
                }
                if (!required && value === template.id) {
                  onChange(null, null);
                  return;
                }
                onChange(template.id, template);
              }}
            />
          ))}
        </div>
      ) : null}

      {!isFetching && value != null && templates.some((item) => item.id === value && !item.runnable) ? (
        <p className={styles.warning}>{t('valueChainMap.agentTemplateRunnableWarning')}</p>
      ) : null}

      {showInsightsNote ? (
        <p className={styles.insightsNote}>{t('valueChainMap.agentTemplateInsightsNote')}</p>
      ) : null}
    </div>
  );
}

EligibleAgentTemplateSelect.defaultProps = {
  uiAgentType: undefined,
  type: undefined,
  subType: undefined,
  label: undefined,
  disabled: false,
  required: true,
  className: undefined,
  skip: false,
};

export default EligibleAgentTemplateSelect;
